import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TicketAttachments — view + add documents on an EXISTING ticket.
 *
 * Used in the Edit modal so files can be added after a ticket is created (the
 * Create wizard stages files pre-creation; this works against a real ticket id).
 * Reuses the same 3-step pipeline (initiate → PUT to Blob → finalize) and the
 * /attachments/{id}/* routes fixed in #9. Uploads happen immediately on pick and
 * the list refetches so the user sees the document land.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '@/lib/i18n';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import { hasPerm, useAuthStore } from '@/store/auth';
const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024; // 25 MB (matches the Create form)
const ATTACHMENT_MAX_COUNT = 10;
const ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb,.ppt,.pptx,.txt,.csv,.zip';
async function uploadOne(ticketId, file) {
    const init = await api.post(`/tickets/${ticketId}/attachments`, {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
    });
    const put = await fetch(init.upload_url, {
        method: init.method || 'PUT',
        headers: { ...(init.headers ?? {}), 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
    });
    if (!put.ok)
        throw new Error('blob upload failed');
    await api.post(`/attachments/${init.attachment_id}/finalize`);
}
export function TicketAttachments({ ticketId }) {
    const qc = useQueryClient();
    const toast = useToast();
    const me = useAuthStore((s) => s.me);
    const canDelete = hasPerm(me, 'ticket.attachment.delete');
    const [busy, setBusy] = useState(false);
    const list = useQuery({
        queryKey: ['ticket-attachments', ticketId],
        queryFn: () => api.get(`/tickets/${ticketId}/attachments`),
    });
    const remove = useMutation({
        mutationFn: (attachmentId) => api.del(`/attachments/${attachmentId}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] });
            toast.success(t('Attachment removed'), t('The document has been deleted from this ticket.'));
        },
        onError: (e) => toast.error(t('Could not delete the document'), e.message),
    });
    const onDelete = (id, filename) => {
        if (window.confirm(`Delete "${filename}"? This removes the document from the ticket.`)) {
            remove.mutate(id);
        }
    };
    const upload = useMutation({
        mutationFn: async (files) => {
            const failed = [];
            for (const file of files) {
                try {
                    await uploadOne(ticketId, file);
                }
                catch {
                    failed.push(file.name);
                }
            }
            return failed;
        },
        onSuccess: (failed) => {
            void qc.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] });
            if (failed.length) {
                alert(`${failed.length} file(s) could not be uploaded: ${failed.join(', ')}.`);
            }
        },
    });
    const current = list.data?.length ?? 0;
    const onPick = async (picked) => {
        const tooBig = picked.filter((f) => f.size > ATTACHMENT_MAX_BYTES);
        if (tooBig.length) {
            alert(`${tooBig.length} file(s) exceed 25 MB and were skipped: ${tooBig.map((x) => x.name).join(', ')}`);
        }
        const ok = picked.filter((f) => f.size <= ATTACHMENT_MAX_BYTES);
        const room = ATTACHMENT_MAX_COUNT - current;
        if (ok.length > room) {
            alert(`A ticket can hold up to ${ATTACHMENT_MAX_COUNT} files. Extra files were skipped.`);
        }
        const toUpload = ok.slice(0, Math.max(0, room));
        if (!toUpload.length)
            return;
        setBusy(true);
        try {
            await upload.mutateAsync(toUpload);
        }
        finally {
            setBusy(false);
        }
    };
    const download = async (id) => {
        try {
            const r = await api.get(`/attachments/${id}/download`);
            window.open(r.download_url, '_blank', 'noopener');
        }
        catch {
            alert('Could not open that file right now. Please try again.');
        }
    };
    return (_jsxs("div", { className: "space-y-2", children: [list.data && list.data.length > 0 && (_jsx("ul", { className: "space-y-1.5", children: list.data.map((a) => (_jsxs("li", { className: "flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/50", children: [_jsx(FileText, { size: 14, className: "text-slate-400" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "truncate font-medium", children: a.original_filename }), _jsxs("div", { className: "text-[11px] text-slate-500", children: [a.content_type || 'unknown type', " \u00B7 ", Math.round(a.size_bytes / 1024), " KB"] })] }), _jsx("button", { type: "button", onClick: () => void download(a.id), className: "rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-600 dark:hover:bg-slate-700", "aria-label": `Download ${a.original_filename}`, title: "Download", children: _jsx(Download, { size: 16 }) }), canDelete && (_jsx("button", { type: "button", onClick: () => onDelete(a.id, a.original_filename), disabled: remove.isPending, className: "rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-50 dark:hover:bg-slate-700", "aria-label": `Delete ${a.original_filename}`, title: "Delete", children: _jsx(Trash2, { size: 16 }) }))] }, a.id))) })), current < ATTACHMENT_MAX_COUNT ? (_jsxs("label", { className: "flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-500 hover:border-brand-400 hover:bg-brand-50/30 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-brand-500 dark:hover:bg-brand-950/30", children: [busy ? _jsx(Loader2, { size: 16, className: "animate-spin text-slate-400" }) : _jsx(Upload, { size: 16, className: "text-slate-400" }), _jsx("span", { className: "flex-1", children: busy ? 'Uploading…' : 'Click to add documents (image, PDF, doc, …)' }), _jsx("input", { type: "file", multiple: true, disabled: busy, className: "sr-only", accept: ACCEPT, onChange: (e) => {
                            const picked = Array.from(e.target.files ?? []);
                            e.target.value = '';
                            if (picked.length)
                                void onPick(picked);
                        } })] })) : (_jsxs("p", { className: "text-[11px] text-slate-400", children: ["Maximum of ", ATTACHMENT_MAX_COUNT, " files reached."] })), _jsxs("p", { className: "text-[11px] text-slate-400", children: ["Optional \u2014 images, PDFs, docs (max 25 MB each, up to ", ATTACHMENT_MAX_COUNT, " files)."] })] }));
}
